// src/theme.ts
import { createTheme } from "@mui/material/styles"
import { loadCustomThemeColors, loadCustomColorsFromStorage } from "@core/utils/themeUtils"
import { OVERLAY_SAFE_TOP_OFFSET, OVERLAY_VIEWPORT_MARGIN } from "@shared/components/modals/overlayDragRules"

declare module "@mui/material/styles" {
  interface Theme {
    custom: {
      borderColor: string
      hoverBg: string
      borderColorTreatmentTag?: string
      bgColorTreatmentTag: string
      borderColorReceiptTag?: string
      bgColorReceiptTag?: string
      borderColorMemoTag?: string
      bgColorMemoTag?: string
      borderColorPatientCard?: string
      bgColorPatientCard?: string
      hoverBgPatientCard?: string
      selectedBgPatientCard?: string
      borderColorInsurance1Tag?: string
      bgColorInsurance1Tag?: string
      borderColorInsurance2Tag?: string
      bgColorInsurance2Tag?: string
      borderColorInsurance3Tag?: string
      bgColorInsurance3Tag?: string
      borderColorInsurance4Tag?: string
      bgColorInsurance4Tag?: string
      borderColorEncounterItemTag?: string
      bgColorEncounterItemTag?: string
      textColorTreatmentTag?: string
      globalBgColor?: string
      panelBgColor?: string
      informationBgColor?: string
      buttonBgColor?: string
      cardHoverColor?: string
      cardNormalColor?: string
      cardSelectedColor?: string
      panelHeaderColor?: string
      subInfoBgColor?: string
      encounterBgColor?: string
      encounterContentsBgColor?: string // 진료 내용 배경색
      infoTableBgColor?: string // 정보 테이블 배경색
      infoTablePastelColor?: string // 정보 테이블 파스텔 색상
      layer1Transparency?: number // 투명도 설정 (기본값: 0.2)
      layer2Transparency?: number // 투명도 설정 (기본값: 0.5)
      layer3Transparency?: number // 투명도 설정 (기본값: 0.8)
      layer4Transparency?: number // 투명도 설정 (기본값: 1.0)
      mainFontColor?: string // 메인 폰트 색상
      subFontColor?: string // 서브 폰트 색상
      topBarHeight?: number // TopBar 높이 설정 (기본값: 80px)
      sideToggleButtonColor?: string // 사이드 토글 버튼 색상
      sideRoundedButtonColor?: string // 사이드 라운드 버튼 색상
      informationTitleFontColor?: string // 정보 폰트 색상
      informationTitleColor?: string // 정보 타이틀 색상
      historyAccentColor?: string // 진료이력 아이콘 및 텍스트 색상
      dashboardTitleFontColor?: string // 대시보드 타이틀 폰트 색상
      transitionHoverColor?: string // 전환 호버 색상
      trialBgColor?: string // 트라이얼 배경 색상
      isDarkMode?: boolean // 다크 모드 여부
      emptyBoxHeight?: number // 대시보드 빈 박스 높이 (기본값: 400px)
      // 배경 이미지 설정
      useBackgroundImage?: boolean // 배경 이미지 사용 여부
      backgroundImagePath?: string // 배경 이미지 경로
      backgroundColor?: string // 배경 색상
    }
  }
  interface ThemeOptions {
    custom?: {
      borderColor: string
      hoverBg: string
      borderColorTreatmentTag?: string
      bgColorTreatmentTag: string
      borderColorReceiptTag?: string
      bgColorReceiptTag?: string
      borderColorMemoTag?: string
      bgColorMemoTag?: string
      borderColorPatientCard?: string
      bgColorPatientCard?: string
      hoverBgPatientCard?: string
      selectedBgPatientCard?: string
      borderColorInsurance1Tag?: string
      bgColorInsurance1Tag?: string
      borderColorInsurance2Tag?: string
      bgColorInsurance2Tag?: string
      borderColorInsurance3Tag?: string
      bgColorInsurance3Tag?: string
      borderColorInsurance4Tag?: string
      bgColorInsurance4Tag?: string
      borderColorEncounterItemTag?: string
      bgColorEncounterItemTag?: string
      textColorTreatmentTag?: string
      globalBgColor?: string
      panelBgColor?: string
      informationBgColor?: string
      buttonBgColor?: string
      cardHoverColor?: string
      cardNormalColor?: string
      cardSelectedColor?: string
      panelHeaderColor?: string
      subInfoBgColor?: string
      encounterBgColor?: string
      encounterContentsBgColor?: string
      layer1Transparency?: number // 투명도 설정 (기본값: 0.2)
      layer2Transparency?: number // 투명도 설정 (기본값: 0.5)
      layer3Transparency?: number // 투명도 설정 (기본값: 0.8)
      layer4Transparency?: number // 투명도 설정 (기본값: 1.0)
      mainFontColor?: string // 메인 폰트 색상
      subFontColor?: string // 서브 폰트 색상
      topBarHeight?: number // TopBar 높이 설정 (기본값: 80px)
      sideToggleButtonColor?: string // 사이드 토글 버튼 색상
      sideRoundedButtonColor?: string // 사이드 라운드 버튼 색상
      informationTitleFontColor?: string // 정보 폰트 색상
      informationTitleColor?: string // 정보 타이틀 색상
      historyAccentColor?: string // 진료이력 아이콘 및 텍스트 색상
      dashboardTitleFontColor?: string // 대시보드 타이틀 폰트 색상
      transitionHoverColor?: string // 전환 호버 색상
      trialBgColor?: string // 트라이얼 배경 색상
      isDarkMode?: boolean // 다크 모드 여부
      emptyBoxHeight?: number // 대시보드 빈 박스 높이 (기본값: 400px)
      // 배경 이미지 설정
      useBackgroundImage?: boolean // 배경 이미지 사용 여부
      backgroundImagePath?: string // 배경 이미지 경로
      backgroundColor?: string // 배경 색상
    }
  }
}

const theme = createTheme({
  typography: {
    // 기본은 SUIT를 유지하되, macOS/iOS 웹에서는 전역 CSS 변수로 애플 시스템 폰트를 우선 적용
    fontFamily:
      'var(--app-font-family, "SUIT", -apple-system, BlinkMacSystemFont, system-ui, Roboto, "Noto Sans KR", "Malgun Gothic", Arial, sans-serif)',
    fontWeightLight: 300,
    fontWeightRegular: 500,
    fontWeightMedium: 600,
    fontWeightBold: 700,
  },
  palette: {
    primary: { main: "#0196da", contrastText: "#fff" },
    secondary: { main: "#6cbba9", contrastText: "#fff" },
    background: { default: "#F9FAFB", paper: "#fff" },
    text: { primary: "#222", secondary: "#5b90b7" },
    divider: "#ccc",
  },
  custom: (() => {
    // 외부 설정 파일에서 색상 로드
    const externalColors = loadCustomThemeColors()
    // 로컬 스토리지에서 사용자 커스텀 색상 로드 (우선순위)
    const userCustomColors = loadCustomColorsFromStorage()

    // 설정 병합 (사용자 커스텀 > 외부 설정 > 기본값)
    return {
      ...externalColors,
      ...userCustomColors,
    }
  })(),
  // 컴포넌트 기본 스타일은 건드리지 않습니다!
  components: {
    // 모든 TextField 기본 spellCheck 비활성화
    MuiTextField: {
      defaultProps: {
        inputProps: {
          spellCheck: false,
        },
      },
    },
    // Autocomplete, Select 등 InputBase 계열도 같이 적용
    MuiInputBase: {
      defaultProps: {
        inputProps: {
          spellCheck: false,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          marginTop: OVERLAY_SAFE_TOP_OFFSET,
          marginRight: OVERLAY_VIEWPORT_MARGIN,
          marginBottom: OVERLAY_VIEWPORT_MARGIN,
          marginLeft: OVERLAY_VIEWPORT_MARGIN,
          maxHeight: `calc(100% - ${OVERLAY_SAFE_TOP_OFFSET + OVERLAY_VIEWPORT_MARGIN * 2}px)`,
        },
        paperFullScreen: {
          margin: 0,
          maxHeight: "none",
        },
      },
    },
  },
})

export default theme

